import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map, Observable } from 'rxjs';

@Injectable()
export class AnchorService {
  #activatedRoute = inject(ActivatedRoute);
  #document = inject(DOCUMENT);
  #window = this.#document.defaultView as Window;

  /**
   * Monitors URL anchor changes and redirects to a specified block if the anchor matches.
   * This method should be used within `ngAfterViewInit()` of a component to ensure
   * that the redirection can occur. If placed outside of `ngAfterViewInit()`, the component's
   * view might not be fully initialized, and the redirection may not work as expected.
   *
   * @param anchor The anchor part of the URL to monitor.
   * @param redirectToBlockId The ID of the block to scroll to if the URL's anchor matches.
   * @returns An Observable that completes when the redirection has been executed.
   */
  monitorUrlAnchor(anchor: string, redirectToBlockId: string): Observable<void> {
    return this.#activatedRoute.fragment.pipe(
      filter((fragment) => !!fragment),
      map((fragment) => {
        if (anchor !== fragment) return;
        this.#window.setTimeout(() => {
          this.#goToSection(redirectToBlockId);
        }, 500);
      })
    );
  }

  #goToSection(blockId: string) {
    const element = this.#document.getElementById(blockId);
    if (element) {
      const rect = element.getBoundingClientRect();

      const scrollOptions: ScrollToOptions = {
        left: rect.left,
        top: rect.top - 72, // rect.top - $s-nav-desktop-scroll-height
        behavior: 'smooth',
      };

      this.#window.scrollTo(scrollOptions);
    }
  }
}
